import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { BoldIcon, ItalicIcon, ListBulletIcon, NumberedListIcon, LinkIcon } from '@heroicons/react/24/outline';
import { parseMarkdownLite, type BlockNode, type InlineNode } from '../utils/markdownLite';
import Modal from './Modal';

// What-you-see-is-what-you-get editor for the narrative fields. Formatting applies as you type
// (bold, italic, bulleted and numbered lists, links); Enter continues a list. The value handed
// back is Markdown-lite (utils/markdownLite.ts), so storage, the case page and the PDF are the
// same as for text typed anywhere else. Drop-in for <textarea>: onChange receives the new text.
type Props = Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> & {
  value: string | null | undefined;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  disabled?: boolean;
};

// ---- Markdown-lite → HTML (what the editor shows) ----
const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const inlineHtml = (nodes: InlineNode[]): string => nodes.map((n) => {
  switch (n.type) {
    case 'strong': return `<b>${inlineHtml(n.children)}</b>`;
    case 'em': return `<i>${inlineHtml(n.children)}</i>`;
    case 'link': return `<a href="${esc(n.href)}">${inlineHtml(n.children)}</a>`;
    default: return esc(n.text).replace(/\n/g, '<br>');
  }
}).join('');
const blocksHtml = (blocks: BlockNode[]): string => blocks.map((b) => {
  if (b.type === 'paragraph') return `<p>${inlineHtml(b.children) || '<br>'}</p>`;
  const tag = b.ordered ? 'ol' : 'ul';
  return `<${tag}>${b.items.map((it) => `<li>${inlineHtml(it)}</li>`).join('')}</${tag}>`;
}).join('');
export const markdownToHtml = (text: string) => blocksHtml(parseMarkdownLite(text));

// ---- HTML (editor DOM, or a paste) → Markdown-lite ----
const BLOCK_TAGS = new Set(['P', 'DIV', 'UL', 'OL', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE', 'TABLE', 'TR', 'SECTION', 'ARTICLE']);
const wrap = (inner: string, marker: string) => {
  const m = /^(\s*)([\s\S]*?)(\s*)$/.exec(inner)!;
  return m[2] ? `${m[1]}${marker}${m[2]}${marker}${m[3]}` : inner;
};
const isBold = (el: HTMLElement) => el.tagName === 'B' || el.tagName === 'STRONG' || /^(bold|[6-9]00)$/.test(el.style.fontWeight);
const isItalic = (el: HTMLElement) => el.tagName === 'I' || el.tagName === 'EM' || el.style.fontStyle === 'italic';

const inlineMd = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) return (node.textContent || '').replace(/ /g, ' ').replace(/[\r\n\t]+/g, ' ');
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const el = node as HTMLElement;
  if (el.tagName === 'BR') return '\n';
  const inner = Array.from(el.childNodes).map(inlineMd).join('');
  if (el.tagName === 'A') {
    const href = el.getAttribute('href') || '';
    const text = inner.trim() || href;
    return /^(https?:\/\/|mailto:)/.test(href) ? `[${text}](${href})` : text;
  }
  let out = inner;
  if (isItalic(el)) out = wrap(out, '*');
  if (isBold(el)) out = wrap(out, '**');
  return out;
};

const blocksMd = (root: Node): string => {
  const blocks: string[] = [];
  let run = '';
  const flushRun = () => { const t = run.replace(/^\n+|\n+$/g, ''); if (t.trim()) blocks.push(t); run = ''; };
  const walk = (parent: Node, listTag: 'ul' | 'ol' | null, itemNo: { n: number }) => {
    Array.from(parent.childNodes).forEach((child) => {
      const el = child.nodeType === Node.ELEMENT_NODE ? (child as HTMLElement) : null;
      if (!el || !BLOCK_TAGS.has(el.tagName)) { run += inlineMd(child); return; }
      flushRun();
      if (el.tagName === 'UL' || el.tagName === 'OL') {
        const items: string[] = [];
        const tag = el.tagName === 'OL' ? 'ol' : 'ul';
        Array.from(el.children).forEach((li) => {
          if (li.tagName !== 'LI') { walk(li, tag, itemNo); return; }
          // Nested lists flatten into the parent list.
          const nested = Array.from(li.children).filter((c) => c.tagName === 'UL' || c.tagName === 'OL');
          nested.forEach((c) => c.remove());
          const text = inlineMd(li).replace(/\n+/g, ' ').trim();
          if (text) items.push(tag === 'ol' ? `${items.length + 1}. ${text}` : `- ${text}`);
          nested.forEach((c) => {
            Array.from(c.children).forEach((nli) => {
              const t = inlineMd(nli).replace(/\n+/g, ' ').trim();
              if (t) items.push(tag === 'ol' ? `${items.length + 1}. ${t}` : `- ${t}`);
            });
          });
        });
        if (items.length) blocks.push(items.join('\n'));
        return;
      }
      if (el.tagName === 'LI') { const t = inlineMd(el).trim(); if (t) blocks.push(listTag === 'ol' ? `${itemNo.n += 1}. ${t}` : `- ${t}`); return; }
      // Paragraph-like block: its own inline run, unless it only wraps further blocks.
      const hasBlockChildren = Array.from(el.children).some((c) => BLOCK_TAGS.has(c.tagName));
      if (hasBlockChildren) { walk(el, listTag, itemNo); flushRun(); return; }
      run = inlineMd(el);
      flushRun();
    });
  };
  walk(root, null, { n: 0 });
  flushRun();
  // Merge consecutive list blocks of the same kind that came from separate wrappers.
  return blocks.join('\n\n').replace(/\n{3,}/g, '\n\n').trim();
};

export const htmlToMarkdown = (html: string) => blocksMd(new DOMParser().parseFromString(html, 'text/html').body);

const exec = (cmd: string, arg?: string) => { document.execCommand(cmd, false, arg); };

const RichTextEditor: React.FC<Props> = ({ value, onChange, className = '', rows = 3, id, placeholder, disabled, style, ...rest }) => {
  const ref = useRef<HTMLDivElement>(null);
  const lastEmitted = useRef<string>('');
  const [empty, setEmpty] = useState(!(value || '').trim());
  const [state, setState] = useState({ bold: false, italic: false, ul: false, ol: false });
  const text = value || '';

  // Load the value into the editor only when it changed from outside (initial load, autosave
  // restore, AI undo) — never on our own keystrokes, or the caret would jump.
  useLayoutEffect(() => {
    if (!ref.current || text === lastEmitted.current) return;
    ref.current.innerHTML = markdownToHtml(text);
    lastEmitted.current = text;
    setEmpty(!text.trim());
  }, [text]);

  const emit = useCallback(() => {
    if (!ref.current) return;
    const md = blocksMd(ref.current);
    setEmpty(!md.trim());
    if (md === lastEmitted.current) return;
    lastEmitted.current = md;
    onChange(md);
  }, [onChange]);

  const refreshState = useCallback(() => {
    if (!ref.current || !ref.current.contains(document.activeElement)) return;
    setState({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      ul: document.queryCommandState('insertUnorderedList'),
      ol: document.queryCommandState('insertOrderedList'),
    });
  }, []);
  useEffect(() => {
    document.addEventListener('selectionchange', refreshState);
    return () => document.removeEventListener('selectionchange', refreshState);
  }, [refreshState]);

  const run = (cmd: string, arg?: string) => {
    if (disabled) return;
    ref.current?.focus();
    exec(cmd, arg);
    emit();
    refreshState();
  };

  // Link dialog: the app's Modal, not the browser prompt. The editor loses its selection while
  // the dialog has focus, so the range is saved on open and restored before inserting.
  const [linkDialog, setLinkDialog] = useState<{ text: string; url: string; hadSelection: boolean } | null>(null);
  const savedRange = useRef<Range | null>(null);
  const link = () => {
    if (disabled) return;
    ref.current?.focus();
    const sel = window.getSelection();
    savedRange.current = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
    const selected = sel ? sel.toString().trim() : '';
    const isUrl = /^(https?:\/\/|mailto:)\S+$/.test(selected);
    setLinkDialog({ text: isUrl ? '' : selected, url: isUrl ? selected : 'https://', hadSelection: !!selected && !isUrl });
  };
  const closeLinkDialog = useCallback(() => setLinkDialog(null), []);
  const insertLink = () => {
    if (!linkDialog) return;
    const url = linkDialog.url.trim();
    const text = linkDialog.text.trim() || url;
    const { hadSelection } = linkDialog;
    setLinkDialog(null);
    // The Modal hands focus back to the toolbar button as it closes; take it back afterwards.
    window.setTimeout(() => {
      const el = ref.current;
      if (!el || !url) return;
      el.focus();
      const sel = window.getSelection();
      if (sel && savedRange.current) { sel.removeAllRanges(); sel.addRange(savedRange.current); }
      if (hadSelection && text === (sel ? sel.toString().trim() : '')) exec('createLink', url);
      else exec('insertHTML', `<a href="${esc(url)}">${esc(text)}</a>&nbsp;`);
      savedRange.current = null;
      emit();
    }, 0);
  };
  const linkValid = !!linkDialog && /^(https?:\/\/\S+\.\S+|mailto:\S+@\S+)$/.test(linkDialog.url.trim());

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); link(); }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const plain = e.clipboardData.getData('text/plain');
    // Pasted content goes through Markdown-lite, which keeps bold/italic/lists/links and drops
    // everything else (colours, fonts, tables, images).
    const md = html ? htmlToMarkdown(html) : plain;
    if (!md) return;
    const isBlocky = /\n/.test(md);
    exec('insertHTML', isBlocky ? markdownToHtml(md) : inlineHtml(parseMarkdownLite(md).flatMap((b) => (b.type === 'paragraph' ? b.children : []))));
    emit();
  };

  const btn = (on: boolean) => `p-1 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:hover:bg-transparent ${on ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white' : ''}`;
  const tools: Array<{ icon: React.ReactNode; title: string; on: boolean; go: () => void }> = [
    { icon: <BoldIcon className="h-4 w-4" />, title: 'Bold (Ctrl/⌘+B)', on: state.bold, go: () => run('bold') },
    { icon: <ItalicIcon className="h-4 w-4" />, title: 'Italic (Ctrl/⌘+I)', on: state.italic, go: () => run('italic') },
    { icon: <ListBulletIcon className="h-4 w-4" />, title: 'Bulleted list', on: state.ul, go: () => run('insertUnorderedList') },
    { icon: <NumberedListIcon className="h-4 w-4" />, title: 'Numbered list', on: state.ol, go: () => run('insertOrderedList') },
    { icon: <LinkIcon className="h-4 w-4" />, title: 'Link (Ctrl/⌘+K)', on: false, go: link },
  ];

  // The caller's margin belongs to the whole control; the rest styles the box itself.
  const margin = (className.match(/\bmt-\d\b/) || ['mt-1'])[0];
  const boxClass = className.replace(/\bmt-\d\b/, '').replace(/\bblock\b/, '').trim();
  const minLines = Math.max(rows + 2, 4);

  const field = 'mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm text-sm focus:border-emerald-500 focus:ring-emerald-500';
  return (
    <div className={margin}>
      {linkDialog && (
        <Modal title="Add a link" onClose={closeLinkDialog} size="sm">
          {/* Not a <form>: the editor lives inside the case form, and forms don't nest. */}
          <div className="space-y-3" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (linkValid) insertLink(); } }}>
            <div>
              <label htmlFor={`${id || 'rte'}-link-url`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Address</label>
              <input id={`${id || 'rte'}-link-url`} type="url" autoFocus value={linkDialog.url}
                onChange={(e) => setLinkDialog({ ...linkDialog, url: e.target.value })}
                onFocus={(e) => e.target.select()} placeholder="https://" className={field} />
            </div>
            <div>
              <label htmlFor={`${id || 'rte'}-link-text`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">Text to show</label>
              <input id={`${id || 'rte'}-link-text`} type="text" value={linkDialog.text}
                onChange={(e) => setLinkDialog({ ...linkDialog, text: e.target.value })}
                placeholder="Leave empty to show the address" className={field} />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={closeLinkDialog}
                className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
              <button type="button" onClick={insertLink} disabled={!linkValid}
                className="px-3 py-1.5 text-sm font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed">Insert link</button>
            </div>
          </div>
        </Modal>
      )}
      <div className="flex items-center gap-0.5 mb-1" role="toolbar" aria-label="Formatting">
        {tools.map((t) => (
          <button key={t.title} type="button" title={t.title} aria-label={t.title} aria-pressed={t.on} className={btn(t.on)}
            disabled={disabled} onMouseDown={(e) => e.preventDefault()} onClick={t.go}>
            {t.icon}
          </button>
        ))}
      </div>
      <div className="relative">
        {empty && placeholder && (
          <span aria-hidden="true" className="pointer-events-none absolute left-3 top-2 text-sm text-gray-400 dark:text-gray-500">{placeholder}</span>
        )}
        <div ref={ref} id={id} role="textbox" aria-multiline="true" tabIndex={disabled ? -1 : 0}
          contentEditable={!disabled} suppressContentEditableWarning
          className={`${boxClass} border px-3 py-2 text-sm text-gray-900 dark:text-gray-100 dark:bg-gray-700 overflow-auto outline-none focus:ring-1
            [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_p]:min-h-[1.25rem] [&_a]:underline [&_a]:text-emerald-700 dark:[&_a]:text-emerald-400 ${disabled ? 'opacity-60' : ''}`}
          style={{ minHeight: `${minLines * 1.375 + 1}rem`, ...style }}
          onInput={emit} onBlur={emit} onKeyDown={onKeyDown} onPaste={onPaste} onFocus={refreshState} onKeyUp={refreshState} onMouseUp={refreshState}
          {...rest} />
      </div>
    </div>
  );
};

export default RichTextEditor;
